import { useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { PinPad } from "@/components/PinLogin";
import { KeyRound } from "lucide-react";

export default function PinSetup({ user, onUpdated }) {
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const reset = () => { setPin(""); setConfirm(""); setStep(0); };
  const save = async () => {
    if (pin !== confirm) { toast.error("The two PINs didn't match. Let's try again."); reset(); return; }
    setSaving(true);
    try { await api.put("/auth/pin", { pin }); toast.success("Your PIN is set. You can now sign in with it."); reset(); onUpdated?.(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); reset(); }
    finally { setSaving(false); }
  };
  const remove = async () => { await api.delete("/auth/pin"); toast.success("PIN removed"); onUpdated?.(); };

  return (
    <section data-testid="pin-setup" className="bg-white border border-[#E2D9CE] rounded-2xl p-6 sm:p-8 mt-8">
      <div className="flex items-center gap-2 mb-1"><KeyRound className="w-6 h-6 text-[#2E7D6B]" /><h2 className="text-xl font-display font-bold text-slate-900">Easy sign-in with a 4-digit PIN</h2></div>
      <p className="text-slate-600 mb-5">No more typing a password. Sign in with your phone number{user.phone ? ` (${user.phone})` : ""} or email and your PIN.</p>
      {user.has_pin && step === 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span data-testid="pin-status" className="text-[#1F5F4F] font-semibold">PIN is set ✓</span>
          <button data-testid="pin-remove-btn" type="button" onClick={remove} className="text-red-600 font-semibold underline underline-offset-4 min-h-[44px]">Remove PIN</button>
        </div>
      )}
      <p className="text-center text-lg font-semibold text-slate-800 mb-3">{step === 0 ? (user.has_pin ? "Choose a new PIN" : "Choose your PIN") : "Type it once more"}</p>
      {step === 0 ? <PinPad value={pin} onChange={setPin} testId="pin-new" /> : <PinPad value={confirm} onChange={setConfirm} testId="pin-confirm" />}
      <div className="flex gap-3 mt-5 max-w-xs mx-auto">
        {step === 0 ? (
          <button data-testid="pin-next-btn" type="button" disabled={pin.length !== 4} onClick={() => setStep(1)} className="flex-1 bg-[#2E7D6B] disabled:opacity-50 text-white font-semibold min-h-[56px] rounded-xl text-lg">Next</button>
        ) : (
          <>
            <button data-testid="pin-back-btn" type="button" onClick={reset} className="flex-1 border-2 border-[#E2D9CE] font-semibold min-h-[56px] rounded-xl text-lg">Start over</button>
            <button data-testid="pin-save-btn" type="button" disabled={confirm.length !== 4 || saving} onClick={save} className="flex-1 bg-[#2E7D6B] disabled:opacity-50 text-white font-semibold min-h-[56px] rounded-xl text-lg">{saving ? "Saving…" : "Save PIN"}</button>
          </>
        )}
      </div>
    </section>
  );
}
